"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";

import { navigation } from "@/lib/landing/navigation";

const LOGIN_URL = "https://exam.theconclusiondaily.com/login";
const SIGNUP_URL = "https://exam.theconclusiondaily.com/signup";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  const closeMobileMenu = () => setMobileOpen(false);

  const headerText = scrolled ? "text-brand" : "text-white";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "border-b border-slate-200/80 bg-white/95 shadow-sm backdrop-blur-xl"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-2 px-3 sm:gap-3 sm:px-6 lg:h-20 lg:px-8">
          <Link
            href="/"
            aria-label="The Conclusion Daily home"
            className="flex shrink-0 items-center gap-2.5 sm:gap-3"
            onClick={closeMobileMenu}
          >
            <Image
              src="/logo.png"
              alt=""
              width={52}
              height={52}
              priority
              className="h-10 w-10 shrink-0 object-contain sm:h-12 sm:w-12"
            />
            <span className="flex shrink-0 flex-col whitespace-nowrap leading-tight">
              <span
                className={`whitespace-nowrap text-[11px] font-black tracking-[0.015em] sm:text-sm md:text-base ${headerText}`}
              >
                THE CONCLUSION DAILY
              </span>
              <span className="mt-1 text-[9px] uppercase tracking-[0.22em] text-[#D4AF37] sm:text-[10px] sm:tracking-[0.28em]">
                Hope &amp; Faith
              </span>
            </span>
          </Link>

          <nav
            aria-label="Main navigation"
            className="hidden items-center gap-6 xl:gap-8 lg:flex"
          >
            {navigation.map((item) => (
              <Link
                key={item.title}
                href={item.href}
                className={`relative whitespace-nowrap py-2 text-sm font-semibold transition-colors after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#D4AF37] after:transition-all hover:after:w-full ${
                  scrolled
                    ? "text-slate-700 hover:text-brand"
                    : "text-white/90 hover:text-[#F2D27A]"
                }`}
              >
                {item.title}
              </Link>
            ))}
          </nav>

          <div className="hidden shrink-0 items-center gap-4 lg:flex">
            <Link
              href="#institutes"
              className={`whitespace-nowrap text-sm font-semibold transition-colors ${
                scrolled
                  ? "text-slate-700 hover:text-brand"
                  : "text-white/90 hover:text-[#F2D27A]"
              }`}
            >
              For Institutes
            </Link>
            <Link
              href={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`whitespace-nowrap text-sm font-semibold transition-colors ${
                scrolled
                  ? "text-brand hover:text-[#B08D20]"
                  : "text-white hover:text-[#F2D27A]"
              }`}
            >
              Login
            </Link>
            <Link
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-xl bg-brand px-5 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition duration-200 hover:-translate-y-0.5 hover:bg-brand-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2"
            >
              Explore Exams
            </Link>
          </div>

          <button
            type="button"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="tcd-mobile-navigation"
            onClick={() => setMobileOpen((open) => !open)}
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors lg:hidden ${
              scrolled
                ? "text-brand hover:bg-slate-100"
                : "text-white hover:bg-white/10"
            }`}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="tcd-mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[60] bg-slate-950/55 backdrop-blur-sm lg:hidden"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeMobileMenu();
            }}
          >
            <motion.nav
              id="tcd-mobile-navigation"
              aria-label="Mobile navigation"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.24, ease: "easeOut" }}
              className="absolute right-0 top-0 flex h-full w-[min(88vw,380px)] flex-col overflow-y-auto bg-white px-5 pb-8 pt-5 shadow-2xl sm:px-7"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-5">
                <Link
                  href="/"
                  onClick={closeMobileMenu}
                  className="flex shrink-0 items-center gap-3"
                >
                  <Image
                    src="/logo.png"
                    alt=""
                    width={44}
                    height={44}
                    className="h-10 w-10 shrink-0 object-contain"
                  />
                  <span className="flex shrink-0 flex-col whitespace-nowrap leading-tight">
                    <span className="whitespace-nowrap text-[11px] font-black tracking-tight text-brand sm:text-sm">
                      THE CONCLUSION DAILY
                    </span>
                    <span className="mt-1 text-[9px] uppercase tracking-[0.24em] text-[#B08D20]">
                      Hope &amp; Faith
                    </span>
                  </span>
                </Link>
                <button
                  type="button"
                  aria-label="Close navigation menu"
                  onClick={closeMobileMenu}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-brand transition hover:bg-slate-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={closeMobileMenu}
                    className="flex min-h-12 items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-[#D4AF37]/60 hover:bg-amber-50/50 hover:text-brand"
                  >
                    {item.title}
                    <ChevronRight aria-hidden="true" className="h-4 w-4 text-[#B08D20]" />
                  </Link>
                ))}
                <Link
                  href="#institutes"
                  onClick={closeMobileMenu}
                  className="flex min-h-12 items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-[#D4AF37]/60 hover:bg-amber-50/50 hover:text-brand"
                >
                  For Institutes
                  <ChevronRight aria-hidden="true" className="h-4 w-4 text-[#B08D20]" />
                </Link>
              </div>

              <div className="grid gap-3 border-t border-slate-100 pt-5">
                <Link
                  href={LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="rounded-xl border border-brand px-5 py-3.5 text-center text-sm font-bold text-brand transition hover:bg-brand hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                >
                  Login
                </Link>
                <Link
                  href={SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="rounded-xl bg-brand px-5 py-3.5 text-center text-sm font-bold text-white shadow-md transition hover:bg-brand-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2"
                >
                  Explore Exams
                </Link>
              </div>
              <p className="pt-5 text-center text-xs text-slate-500">
                Learn. Compete. Improve.
              </p>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
